<template>
  <div class="content-main">
    <div class="content">
      <div class="header-section">
        <h1 class="title">{{ $t("message.forms_page.title") }}</h1>
      </div>

      <div class="tabs-section">
        <div class="tabs-header">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'forms' }"
            @click="activeTab = 'forms'"
          >
            {{ $t("message.forms_page.forms") }}
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'submissions' }"
            @click="activeTab = 'submissions'"
          >
            {{ $t("message.forms_page.submissions") }}
          </button>
        </div>

        <!-- TAB 1: FORMS -->
        <div v-if="activeTab === 'forms'" class="tab-content">
          <div class="action-bar">
            <button class="btn-success-button" @click="createNewForm">
              {{ $t("message.forms_page.new_form") }}
            </button>
          </div>

          <FormsListTable
            :forms="forms"
            :loading="loading"
            @copy-link="copyFormLink"
            @view-submissions="selectFormForSubmissions"
            @edit-form="editForm"
            @delete-form="removeForm"
          />

          <FormEditorPanel
            :form="editingForm"
            :show-editor="showEditor"
            @close="cancelEditor"
            @save="saveForm"
          />
        </div>

        <!-- TAB 2: SUBMISSIONS -->
        <div v-if="activeTab === 'submissions'" class="tab-content">
          <AllSubmissionsView
            :submissions="submissions"
            :forms="forms"
            :loading="submissionsLoading"
          />
        </div>
      </div>

      <!-- Customer Modal -->
      <InfoautoCreateCustomerModal
        v-if="showCreateCustomerModal"
        @close="closeCustomerModal"
        @newCustomer="onNewCustomer"
        ref="customerModal"
      />
    </div>
  </div>
</template>

<script>
import {
  getForms,
  createForm,
  updateForm,
  getAllSubmissions,
  deleteForm
} from "@/api/store/FormsService.js";
import FormsListTable from "@/components/forms/FormsListTable.vue";
import AllSubmissionsView from "@/components/forms/AllSubmissionsView.vue";
import FormEditorPanel from "@/components/forms/FormEditorPanel.vue";
import InfoautoCreateCustomerModal from "@/components/customer/InfoautoCreateCustomerModal.vue";

export default {
  name: "FormSellerCustomerPage",
  components: {
    FormsListTable,
    AllSubmissionsView,
    FormEditorPanel,
    InfoautoCreateCustomerModal
  },
  data() {
    return {
      activeTab: "submissions",
      forms: [],
      submissions: [],
      loading: false,
      submissionsLoading: false,
      showEditor: false,
      showCreateCustomerModal: false,
      submissionDataForCustomer: null,
      editingForm: {
        id: null,
        name: "",
        definition: { fields: [], termsConditions: "", language: "en" }
      }
    };
  },
  computed: {
    currentUserId() {
      const currentUser = this.$store.getters["auth/currentUser"];
      return currentUser ? currentUser.id : null;
    },
    storeUrl() {
      const store = this.$store.getters["general/getActiveStore"];
      const url = store && store.url ? store.url : "";
      return url.endsWith("/") ? url.slice(0, -1) : url;
    }
  },
  mounted() {
    this.loadForms();
    this.loadAllSubmissions();
  },
  methods: {
    async loadForms() {
      this.loading = true;
      try {
        this.forms = await getForms();
        this.forms.forEach(form => {
          if (form.definition && form.definition.fields) {
            form.definition.fields.forEach(field => {
              if (!field.options) {
                field.options = [];
              }
            });
          }
          if (!form.definition.termsConditions) {
            form.definition.termsConditions = "";
          }
        });
      } catch (e) {
        this.$toast.open({
          message: "Error loading forms",
          type: "error",
          position: "top"
        });
      } finally {
        this.loading = false;
      }
    },

    async loadAllSubmissions() {
      this.submissionsLoading = true;
      try {
        this.submissions = await getAllSubmissions();
      } catch (e) {
        this.$toast.open({
          message: "Error loading submissions",
          type: "error",
          position: "top"
        });
      } finally {
        this.submissionsLoading = false;
      }
    },

    selectFormForSubmissions() {
      this.activeTab = "submissions";
    },

    editForm(form) {
      this.editingForm = JSON.parse(JSON.stringify(form));
      this.editingForm.definition.fields.forEach(field => {
        if (!field.options) {
          field.options = [];
        }
      });
      if (!this.editingForm.definition.termsConditions) {
        this.editingForm.definition.termsConditions = "";
      }
      this.showEditor = true;
    },

    async saveForm(form) {
      try {
        if (!form.name || form.name.trim() === "") {
          this.$toast.open({
            message: "Form name is required",
            type: "warning",
            position: "top"
          });
          return;
        }

        if (form.definition.fields.length === 0) {
          this.$toast.open({
            message: "Add at least one field",
            type: "warning",
            position: "top"
          });
          return;
        }

        if (form.id) {
          await updateForm(form.id, form);
        } else {
          await createForm(form);
        }

        this.$toast.open({
          message: "Form saved successfully",
          type: "success",
          position: "top"
        });

        await this.loadForms();
        this.cancelEditor();
      } catch (e) {
        this.$toast.open({
          message: e.message || "Error saving form",
          type: "error",
          position: "top"
        });
      }
    },

    createNewForm() {
      this.editingForm = {
        id: null,
        name: "",
        definition: { fields: [], termsConditions: "", language: "en" }
      };
      this.showEditor = true;
    },

    cancelEditor() {
      this.showEditor = false;
      this.editingForm = {
        id: null,
        name: "",
        definition: { fields: [], termsConditions: "" }
      };
    },

    copyFormLink(form) {
      if (!this.currentUserId) {
        this.$toast.open({
          message: "Error: User ID not found",
          type: "error",
          position: "top"
        });
        return;
      }

      const link = `${this.storeUrl}/form/${form.uuid}?seller=${this.currentUserId}`;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          this.$toast.open({
            message: this.$t("message.forms_page.link_copied"),
            type: "success",
            position: "top"
          });
        })
        .catch(() => {
          this.$toast.open({
            message: "Error copying link",
            type: "error",
            position: "top"
          });
        });
    },

    addCustomerFromSubmission(submission) {
      const responses = submission.responses;
      this.submissionDataForCustomer = {
        name: responses.name || "",
        last_name: responses.last_name || "",
        email: responses.email || "",
        phone: responses.phone || ""
      };
      this.showCreateCustomerModal = true;

      this.$nextTick(() => {
        if (this.$refs.customerModal) {
          this.$refs.customerModal.newCustomer.name = this.submissionDataForCustomer.name;
          this.$refs.customerModal.newCustomer.surname = this.submissionDataForCustomer.last_name;
          this.$refs.customerModal.newCustomer.email = this.submissionDataForCustomer.email;
          this.$refs.customerModal.newCustomer.phone = this.submissionDataForCustomer.phone;
        }
      });
    },

    closeCustomerModal() {
      this.showCreateCustomerModal = false;
      this.submissionDataForCustomer = null;
    },

    onNewCustomer() {
      this.closeCustomerModal();
    },

    async removeForm(form) {
      if (
        !confirm(
          `¿Estás seguro de que quieres eliminar el formulario "${form.name}"?`
        )
      ) {
        return;
      }

      try {
        await deleteForm(form.id);
        this.$toast.open({
          message: "Form deleted successfully",
          type: "success",
          position: "top"
        });
        this.loadForms();
      } catch (e) {
        this.$toast.open({
          message: e.message || "Error deleting form",
          type: "error",
          position: "top"
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  padding: 20px;
}

.header-section {
  margin-bottom: 30px;

  .title {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
  }
}

.tabs-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
}

.tab-btn {
  flex: 1;
  padding: 15px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  color: #666;
  transition: all 0.3s ease;

  &:hover {
    background: #f5f5f5;
  }

  &.active {
    color: #2196f3;
    border-bottom: 3px solid #2196f3;
  }
}

.tab-content {
  padding: 20px;
}

.action-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.btn-success-button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: #4caf50;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background: #45a049;
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}
</style>
